<template>
  <div>
    <div v-show="ready" id="paypaldiv" />
    <div v-if="errorMsg" class="input-error-msg">
      {{ errorMsg }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { useNuxtApp, useRoute, useRuntimeConfig } from 'nuxt/app'
import { loadScript, type PayPalNamespace } from '@paypal/paypal-js'
import { onMounted, onUnmounted, ref } from 'vue'
import { langFallback$Fetch } from '../../../lang/utils/langfetch'
import { BookingPaymentStatusServiceFetch } from '../../payments/payment-processing/BookingPaymentStatusServiceFetch'

type emitType = {
  (e: 'click' | 'closed' | 'ready' | 'cancelled' | 'error'): void
  (e: 'paid', url: string): void
}
const emit = defineEmits<emitType>()
const props = defineProps<{
  currency: string
  locale: string
  paymentId?: string
  bookingId?: string
  shouldClose: boolean
}>()
const paymentStatusService = new BookingPaymentStatusServiceFetch()
const config = useRuntimeConfig()
const ready = ref(false)
const { $i18n, $log } = useNuxtApp()
const errorMsg = ref('')

// Helper function to add context to all log calls
function logWithContext(
  event: string,
  additionalMetadata?: Record<string, unknown>,
): Promise<void> {
  const baseContext = {
    bookingId: props.bookingId,
    paymentId: props.paymentId,
  }
  return $log(event, { ...baseContext, ...additionalMetadata })
}

onMounted(async () => {
  const langCode = useRoute().params?.lang || 'en-gb'

  // Log PayPal button initialization
  await logWithContext('paypal_button_init', {
    currency: props.currency,
    locale: props.locale,
  })

  let paypal: PayPalNamespace | null
  try {
    paypal = await loadScript({
      clientId: config.public.paypalClientId,
      currency: props.currency,
      dataNamespace: 'paypal_sdk',
      // we have to be specific about what to disable. By default they enable them and we only need the yellow button.
      disableFunding:
        'credit,card,bancontact,blik,eps,giropay,ideal,mercadopago,mybank,p24,sepa,sofort,venmo',
      locale: props.locale,
    })
  } catch (e) {
    const error = e as Error
    error.name = `PayPal loadScript error - ${error.name}`
    console.error(error)

    // Log PayPal script load error
    await logWithContext('paypal_script_load_error', {
      error: error.message,
    })

    return
  }
  if (!paypal?.Buttons) {
    console.error('PayPal button not found')

    // Log PayPal buttons not found error
    await logWithContext('paypal_buttons_not_found')

    return
  }
  ready.value = true

  // Log PayPal button ready
  await logWithContext('paypal_button_ready')

  emit('ready')

  try {
    await paypal
      .Buttons({
        style: {
          tagline: false,
          label: 'pay',
        },
        async onClick(_, actions) {
          errorMsg.value = ''
          emit('click')

          // Log PayPal button click
          await logWithContext('paypal_button_click')

          // Add a small delay to allow the parent component to process the click
          await new Promise((resolve) => setTimeout(resolve, 100))

          try {
            await waitForAfterClick()
          } catch (e) {
            if (e instanceof Error) {
              if (e.message.includes('closed')) {
                emit('closed')
                await logWithContext('paypal_button_closed')
              } else if (e.message.includes('timeout')) {
                emit('error')
                await logWithContext('paypal_button_timeout', {
                  error: e.message,
                })
              } else {
                // Log unexpected errors
                await logWithContext('paypal_button_error', {
                  error: e.message,
                })
              }
            }
            return actions.reject()
          }
          return actions.resolve()
        },
        onCancel() {
          emit('cancelled')
          errorMsg.value = $i18n.$t('Payment authorisation cancelled')

          // Log PayPal payment cancelled
          void logWithContext('paypal_payment_cancelled')
        },
        onError(err) {
          emit('error')
          errorMsg.value = $i18n.$t('Something went wrong, try again later')

          // Log PayPal payment error
          void logWithContext('paypal_payment_error', {
            error: err.message || 'Unknown error',
          })

          if (err.message === 'Native popup closed') {
            return
          }
        },
        async createOrder() {
          // Log PayPal create order attempt
          await logWithContext('paypal_create_order_start')

          const orderId = await waitForOrder()

          // Log PayPal order created
          await logWithContext('paypal_order_created', {
            orderId,
          })

          return orderId
        },
        async onApprove(data) {
          // Log PayPal payment approved
          await logWithContext('paypal_payment_approved', {
            orderId: data.orderID,
          })

          const response = await langFallback$Fetch<{
            redirect_url: string
          }>(langCode as string, '/booking/paypal-capture-order/', {
            method: 'post',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              orderId: data.orderID,
              prettyId: props.bookingId,
            }),
          })

          // Log PayPal capture success
          await logWithContext('paypal_capture_success', {
            orderId: data.orderID,
            redirectUrl: response.redirect_url,
            response: response,
          })

          emit('paid', response.redirect_url)
        },
      })
      .render('#paypaldiv')
  } catch (e) {
    const error = e as Error
    error.name = `PayPal paypal.Buttons error - ${error.name}`
    console.error(error)

    // Log PayPal buttons render error
    await logWithContext('paypal_buttons_render_error', {
      error: error.message,
    })
  }
})

function waitForAfterClick() {
  return new Promise((resolve, reject) => {
    if (props.shouldClose) {
      void logWithContext('paypal_wait_click_skip', {
        reason: 'shouldClose is true on start',
      })
      reject(new Error('PayPal button closed by user'))
      return
    }

    // Log wait start
    void logWithContext('paypal_wait_click_start', {
      shouldClose: props.shouldClose,
      hasPaymentId: !!props.paymentId,
      hasBookingId: !!props.bookingId,
    })

    let attempts = 0
    const maxAttempts = 15 // 15 seconds max wait
    const intervalId = setInterval(() => {
      attempts++

      // Log periodic status (every 5 attempts)
      if (attempts % 5 === 0) {
        void logWithContext('paypal_wait_click_status', {
          attempts,
          shouldClose: props.shouldClose,
          hasPaymentId: !!props.paymentId,
          hasBookingId: !!props.bookingId,
        })
      }

      // Check for shouldClose first
      if (props.shouldClose) {
        clearInterval(intervalId)
        void logWithContext('paypal_wait_click_closed', {
          attempts,
          reason: 'shouldClose changed to true',
        })
        reject(new Error('PayPal button closed: shouldClose changed to true'))
        return
      }

      // Then check for successful IDs
      if (props.paymentId && props.bookingId) {
        clearInterval(intervalId)
        void logWithContext('paypal_wait_click_success', {
          attempts,
          elapsedTime: `${attempts}s`,
        })
        resolve(undefined)
        return
      }

      // If we've waited too long, reject
      if (attempts >= maxAttempts) {
        clearInterval(intervalId)
        errorMsg.value = $i18n.$t(
          'Payment processing timed out, please try again',
        )

        // Log PayPal validation timeout
        void logWithContext('paypal_validation_timeout', {
          attempts,
          maxAttempts,
          elapsedTime: `${attempts}s`,
        })

        reject(
          new Error(
            'PayPal payment validation timeout: exceeded max wait time',
          ),
        )
      }
    }, 1000)

    // Cleanup interval on component unmount
    onUnmounted(() => {
      if (intervalId && clearInterval) {
        clearInterval(intervalId)
      }
    })
  })
}

function waitForOrder(): Promise<string> {
  return new Promise((resolve) => {
    let attempts = 0

    // Log order wait starting
    void logWithContext('paypal_wait_order_start', {
      hasPaymentId: !!props.paymentId,
      hasBookingId: !!props.bookingId,
    })

    const intervalId = setInterval(async () => {
      attempts++

      // Log status periodically (every 5 attempts)
      if (attempts % 5 === 0) {
        void logWithContext('paypal_wait_order_status', {
          attempts,
          elapsedTime: `${attempts}s`,
        })
      }

      if (props.paymentId && props.bookingId) {
        // Log attempt to retrieve order
        if (attempts === 1 || attempts % 5 === 0) {
          void logWithContext('paypal_wait_order_poll', {
            attempts,
          })
        }

        const data = await paymentStatusService.get(
          props.paymentId,
          props.bookingId,
        )

        if (data.order?.id) {
          clearInterval(intervalId)

          // Log order found
          void logWithContext('paypal_wait_order_found', {
            attempts,
            elapsedTime: `${attempts}s`,
            orderId: data.order.id,
          })

          resolve(data.order.id)
        } else if (attempts % 5 === 0) {
          // Log no order found on periodic attempts
          void logWithContext('paypal_wait_order_not_found', {
            attempts,
            dataReceived: !!data,
          })
        }
      } else {
        // Log missing IDs
        if (attempts % 5 === 0) {
          void logWithContext('paypal_wait_order_missing_ids', {
            attempts,
            hasPaymentId: !!props.paymentId,
            hasBookingId: !!props.bookingId,
          })
        }
      }

      // Add a safety timeout after 60 seconds
      if (attempts >= 60) {
        clearInterval(intervalId)

        void logWithContext('paypal_wait_order_timeout', {
          attempts,
          elapsedTime: `${attempts}s`,
        })

        // Return empty string to indicate failure - parent code should handle this
        resolve('')
      }
    }, 1000)

    // Cleanup interval on component unmount
    onUnmounted(() => {
      if (intervalId && clearInterval) {
        clearInterval(intervalId)
      }
    })
  })
}
</script>
